import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { track } from '@/shared/analytics';
import { parseScope, toScopeParams, type PerformanceScope } from './scope-params';

type ScopeLevel = 'project' | 'campaign' | 'adset';

function levelOf(scope: PerformanceScope): ScopeLevel {
  if (!scope.campaignId) return 'project';
  return scope.adsetId ? 'adset' : 'campaign';
}

/**
 * Scope lives in the URL alongside `performance-filters`' own params (date range,
 * platforms, channels, `campaigns` multi-select, compare). Writing scope must merge into
 * whatever is already there via the functional `setSearchParams` updater, rather than
 * replacing the whole query string — otherwise drilling into a campaign would silently
 * discard the user's date range and filter selections.
 */
export function useScope(projectId: string): {
  scope: PerformanceScope;
  setScope: (next: PerformanceScope) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  const scope = useMemo(() => parseScope(searchParams), [searchParams]);

  const setScope = useCallback(
    (next: PerformanceScope) => {
      // Compared, and tracked, before the write below: a filters-owned param change
      // (date range, platform, ...) also produces a new `scope` object on every render
      // of this hook (a fresh URLSearchParams, even when campaign/adset didn't move), so
      // an effect over `scope` would fire on every one of those too. Comparing here, at
      // the one place scope is actually written, fires only on a genuine level change.
      if (levelOf(next) !== levelOf(scope)) {
        track('performance_scope_changed', { project_id: projectId, level: levelOf(next) });
      }

      setSearchParams(
        (prev) => {
          const merged = new URLSearchParams(prev);
          /* eslint-disable i18next/no-literal-string -- URL search-param names, not copy */
          merged.delete('campaign');
          merged.delete('adset');
          /* eslint-enable i18next/no-literal-string */
          for (const [key, value] of Object.entries(toScopeParams(next))) {
            merged.set(key, value);
          }
          return merged;
        },
        // `replace` so drilling into a campaign/ad set does not fill the back button
        // with every intermediate scope.
        { replace: true },
      );
    },
    [scope, projectId, setSearchParams],
  );

  return { scope, setScope };
}
